<template>
  <app-content class="h-panel">
    <div class="h-panel-bar"><span class="h-panel-title">账龄分析表</span></div>
    <Row type="flex" :space-x="10" class="margin-right-left margin-top no-print">
      <Cell>
        <Select v-model="subjectId" :datas="subjectList" keyName="id" titleName="name" :width="240"
                :filterable="true" placeholder="选择科目"></Select>
      </Cell>
      <Cell>
        <DatePicker v-model="endDate" :clearable="false" format="YYYY-MM-DD" style="width: 150px;"></DatePicker>
      </Cell>
      <Cell>
        <Checkbox v-model="hideZero">余额为0不显示</Checkbox>
      </Cell>
      <Cell>
        <Button color="primary" icon="fa fa-search" :loading="loading" @click="loadList">查询</Button>
        <Button icon="fa fa-refresh" :loading="loading" @click="loadList">刷新</Button>
        <Button icon="fa fa-download" @click="doExport">导出</Button>
        <Button icon="fa fa-print" @click="doPrint">打印</Button>
      </Cell>
    </Row>
    <div class="h-panel-body" style="overflow-x: auto;">
      <div class="print-header">
        <div class="print-title">账龄分析表{{ selectedSubject ? ' - ' + selectedSubject.name : '' }}</div>
        <div class="print-sub">
          <span>编制单位：{{currentAccountSets?currentAccountSets.companyName:''}}</span>
          <span>截止日期：{{endDate}}</span>
          <span>单位：元</span>
        </div>
      </div>
      <table class="age-table">
        <thead>
        <tr>
          <th rowspan="2">编码</th>
          <th rowspan="2">名称</th>
          <th rowspan="2">期初</th>
          <th rowspan="2">借方</th>
          <th rowspan="2">贷方</th>
          <th rowspan="2">期末</th>
          <th colspan="2">30天以内</th>
          <th colspan="2">30-60天</th>
          <th colspan="2">60-90天</th>
          <th colspan="2">90-180天</th>
          <th colspan="2">180-360天</th>
          <th colspan="2">360-1080天</th>
          <th colspan="2">1080天以上</th>
        </tr>
        <tr>
          <th v-for="n in 7" :key="'a' + n">金额</th>
          <th v-for="n in 7" :key="'b' + n">比例%</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, index) in showList" :key="index">
          <td>{{ item.code }}</td>
          <td :style="{'padding-left': ((item.level || 0) * 10 + 8) + 'px'}">
            <span v-if="item.code" class="text-hover primary-color" @click="goDetail(item)">{{ item.name }}</span>
            <template v-else>{{ item.name }}</template>
          </td>
          <td align="right">{{ item.beginning | numFormat }}</td>
          <td align="right">{{ item.debit | numFormat }}</td>
          <td align="right">{{ item.credit | numFormat }}</td>
          <td align="right">{{ item.ending | numFormat }}</td>
          <template v-for="(bracket, bIndex) in item.brackets">
            <td :key="'v' + bIndex" align="right">{{ bracket.amount | numFormat }}</td>
            <td :key="'r' + bIndex" align="right">{{ ratioText(bracket.ratio) }}</td>
          </template>
        </tr>
        <tr v-if="!showList.length">
          <td colspan="20" class="text-center padding">暂无数据</td>
        </tr>
        </tbody>
        <tfoot v-if="showList.length">
        <tr class="totals-row">
          <td colspan="2" align="center"><b>合计</b></td>
          <td align="right"><b>{{ totals.beginning | numFormat }}</b></td>
          <td align="right"><b>{{ totals.debit | numFormat }}</b></td>
          <td align="right"><b>{{ totals.credit | numFormat }}</b></td>
          <td align="right"><b>{{ totals.ending | numFormat }}</b></td>
          <template v-for="(amount, bIndex) in totals.brackets">
            <td :key="'tv' + bIndex" align="right"><b>{{ amount | numFormat }}</b></td>
            <td :key="'tr' + bIndex" align="right"><b>{{ totals.ending ? Math.round(amount / Math.abs(totals.ending) * 10000) / 100 : '-' }}</b></td>
          </template>
        </tr>
        </tfoot>
      </table>
    </div>
  </app-content>
</template>

<script>
import moment from 'moment';
import {mapState} from 'vuex';
import {exportCsv} from '../../js/common/export-file';

const bracketTitles = ['30天以内', '30-60天', '60-90天', '90-180天', '180-360天', '360-1080天', '1080天以上'];

export default {
  name: 'AgeAnalysis',
  data() {
    return {
      subjectList: [],
      subjectId: null,
      endDate: moment().format('YYYY-MM-DD'),
      hideZero: true,
      dataList: [],
      loading: false
    }
  },
  computed: {
    ...mapState(['currentAccountSets']),
    showList() {
      if (this.hideZero) {
        return this.dataList.filter(item => Number(item.ending) !== 0);
      }
      return this.dataList;
    },
    selectedSubject() {
      return this.subjectList.find(item => item.id === this.subjectId) || null;
    },
    //页脚合计：期初/借贷发生/期末及各账龄段金额合计，比例按合计期末余额重算
    totals() {
      let t = {beginning: 0, debit: 0, credit: 0, ending: 0, brackets: [0, 0, 0, 0, 0, 0, 0]};
      this.showList.forEach(item => {
        t.beginning += Number(item.beginning) || 0;
        t.debit += Number(item.debit) || 0;
        t.credit += Number(item.credit) || 0;
        t.ending += Number(item.ending) || 0;
        (item.brackets || []).forEach((bracket, i) => {
          if (i < 7) {
            t.brackets[i] += Number(bracket.amount) || 0;
          }
        });
      });
      return t;
    }
  },
  methods: {
    ratioText(ratio) {
      if (ratio === null || ratio === undefined || ratio === '') {
        return '-';
      }
      //后端返回的 ratio 恒为分数（金额/|期末|），统一换算为百分比，比例>1 时不再误判
      let value = (Number(ratio) || 0) * 100;
      return Math.round(value * 100) / 100;
    },
    normalize(item) {
      let brackets = item.brackets || item.ages || [];
      if (!Array.isArray(brackets) || !brackets.length) {
        brackets = [];
        for (let i = 1; i <= 7; i++) {
          brackets.push({
            amount: item[`age${i}Amount`] || item[`bracket${i}Amount`] || 0,
            ratio: item[`age${i}Ratio`] || item[`bracket${i}Ratio`] || 0
          });
        }
      }
      brackets = brackets.map(bracket => ({
        amount: bracket.amount || 0,
        ratio: bracket.ratio === undefined ? 0 : bracket.ratio
      }));
      return Object.assign({}, item, {brackets});
    },
    loadSubjects() {
      this.$api.setting.subject.voucherSelect({}).then(({data}) => {
        this.subjectList = data || [];
        //默认应收账款（编码1122开头）
        let receivable = this.subjectList.find(item => item.code && item.code.indexOf('1122') === 0);
        this.subjectId = receivable ? receivable.id : (this.subjectList[0] && this.subjectList[0].id);
        this.loadList();
      });
    },
    loadList() {
      if (!this.subjectId) {
        return;
      }
      this.loading = true;
      this.$api.report.ageAnalysis({
        subjectId: this.subjectId,
        endDate: this.endDate,
        hideZero: this.hideZero
      }).then(({data}) => {
        this.dataList = (data || []).map(item => this.normalize(item));
      }).catch(() => {
        this.$Message.error('账龄分析数据加载失败');
      }).finally(() => this.loading = false);
    },
    doExport() {
      if (!this.showList.length) {
        this.$Message.error('暂无数据可导出');
        return;
      }
      let headers = ['编码', '名称', '期初', '借方', '贷方', '期末'];
      bracketTitles.forEach(title => headers.push(title + '金额', title + '比例%'));
      let rows = this.showList.map(item => {
        let row = [item.code, item.name, item.beginning || '', item.debit || '', item.credit || '', item.ending || ''];
        item.brackets.forEach(bracket => {
          row.push(bracket.amount, this.ratioText(bracket.ratio));
        });
        return row;
      });
      //合计行一并导出
      let totalRow = ['', '合计', this.totals.beginning, this.totals.debit, this.totals.credit, this.totals.ending];
      this.totals.brackets.forEach(amount => {
        totalRow.push(amount, this.totals.ending ? Math.round(amount / Math.abs(this.totals.ending) * 10000) / 100 : '-');
      });
      rows.push(totalRow);
      let subjectName = this.selectedSubject ? this.selectedSubject.name : '';
      exportCsv('账龄分析表' + (subjectName ? '-' + subjectName : '') + (this.endDate ? '-' + this.endDate : ''), headers, rows);
    },
    //联查：跳转明细账并定位到该科目，期间取截止日期所在月份
    goDetail(item) {
      if (!item.code) {
        return;
      }
      this.$router.push({
        name: 'DetailedAccounts',
        query: {
          subjectCode: item.code,
          accountDate: this.endDate
        }
      });
    },
    doPrint() {
      window.print();
    }
  },
  mounted() {
    this.loadSubjects();
  }
}
</script>

<style lang="less" scoped>
.age-table {
  width: 100%;
  border-collapse: collapse;
  white-space: nowrap;

  th, td {
    padding: 6px 8px;
    border: 1px solid #e2e2e2;
    font-size: 12px;
  }

  th {
    background: #f5f5f5;
    text-align: center;
  }

  tbody tr:nth-child(even) {
    background: #fafafa;
  }

  tfoot td {
    background: #f5f5f5;
  }

  .text-hover {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
